/**
 * local-draft — the per-user localStorage draft store shared by every surface that holds
 * unsaved work in memory.
 *
 * The rule this enforces: a page unload must never destroy work the user has already done.
 * Two things end a page's life without warning — a plain reload, and the data-plane 401
 * route-to-login round-trip (`services.ts` `reactToSessionExpiry`), which navigates away and
 * back. A surface whose work product lives only in a signal loses everything to either.
 * So the surface mirrors its whole in-progress snapshot here on EVERY mutation, restores it
 * on mount, and clears it once the work is durable server-side.
 *
 * localStorage is an UNTRUSTED boundary (another tab, an older build, a hand-edited value),
 * so every load is STRUCTURALLY validated by the caller's guard before it is trusted — never
 * cast. This module stays ArkType-free on purpose: it rides in the browser bundle, which the
 * draft-holding surfaces keep free of domain-compile side effects.
 *
 * Every method is failure-tolerant. A storage that is full, disabled, or holding garbage
 * degrades to "no draft" rather than throwing — the point is to PROTECT the work, never to
 * endanger it.
 */

/** A per-user draft slot for one surface's in-progress snapshot. */
export interface LocalDraftStore<T> {
  /** The stored draft, or null when there is none / it fails the guard. */
  load(): T | null
  /** Persist the in-progress draft (best-effort — a full/blocked store never throws up). */
  save(value: T): void
  /** Drop the draft (called once the work is durable, or when server truth supersedes it). */
  clear(): void
}

/** Resolve a Storage, tolerating environments without one (SSR / privacy modes). */
function resolveStorage(explicit?: Storage): Storage | null {
  if (explicit) return explicit
  try {
    return typeof localStorage !== "undefined" ? localStorage : null
  } catch {
    // Accessing localStorage can THROW (sandboxed iframe, disabled cookies) — treat as absent.
    return null
  }
}

/** The namespaced key — per user so two accounts on one browser never cross drafts. */
function draftKey(keyPrefix: string, userId: string | null): string {
  return `${keyPrefix}${userId && userId.length > 0 ? userId : "anon"}`
}

/**
 * Build a draft store over localStorage for one surface.
 *
 * @param keyPrefix versioned, surface-scoped key prefix (e.g. `career:cardgame:draft:v1:`) —
 *   bump the version segment whenever the persisted shape changes, so an old build's drafts
 *   are ignored instead of failing the guard on every load.
 * @param guard total structural guard for the persisted shape; anything it rejects reads as
 *   "no draft".
 */
export function createLocalDraftStore<T>(
  keyPrefix: string,
  userId: string | null,
  guard: (value: unknown) => value is T,
  explicitStorage?: Storage,
): LocalDraftStore<T> {
  const storage = resolveStorage(explicitStorage)
  const key = draftKey(keyPrefix, userId)
  return {
    load() {
      if (!storage) return null
      try {
        const raw = storage.getItem(key)
        if (!raw) return null
        const parsed: unknown = JSON.parse(raw)
        return guard(parsed) ? parsed : null
      } catch {
        return null
      }
    },
    save(value) {
      if (!storage) return
      try {
        storage.setItem(key, JSON.stringify(value))
      } catch {
        // Quota / disabled storage — the in-memory work still stands; we just can't back it up.
      }
    },
    clear() {
      if (!storage) return
      try {
        storage.removeItem(key)
      } catch {
        // ignore — nothing to lose by a failed clear
      }
    },
  }
}
